function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { countBy, compact, isEmpty, mapValues } from 'lodash-es';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { EditLine, EyeLine, TrashLiner24, WideArrowDownward, WideArrowUpward } from 'seagull/icons';
import { Page, Button, Searchbox, TextField, Typography, TableList, ChipButton, DropdownMenu, useStateInRef, PageEmptyState, PageErrorState, TableListActions, ConfirmationModal, PageContentHeader, FilterPickerGroup } from 'seagull';
import pluralize from "../../utils/pluralize";
import { routes } from "../../constants";
import { Intent } from "../../infra/access-control";
import { selectors } from "../../store/state-selectors";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { ServerError } from "../../infra/ErrorResponse";
import { helpArticles } from "../../utils/helpArticles";
import { ContactModel } from "../../models/ContactModel";
import { useAppConfig } from "../../store/store-context";
import PageLoadingState from "../../components/PageLoadingState";
import { ClientPreview } from "../../components/ClientPreview/ClientPreview";
import { handlePromise } from "../../infra/utils";
import ExternalLinkButton from "../../components/ExternalLinkButton";
import { useSortFromUrl } from "../../hooks/useSortFromUrl";
import { NoTemplatesIcon } from "./NoTemplatesIcon";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useBulkActionModal } from "../../hooks/useTerminateAgreement/useBulkActionModal";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { makeSentryIgnoredError } from "../../components/ErrorBoundaryPage";
import { ProposalTemplateSummaryModel, proposalTemplateModel } from "../../models";
import { useEventReporter, useOverlayContext, useIoContext, useStore, useStoreActions } from "../../contexts";
import styles from "./ProposalTemplates.module.scss";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const title = 'Proposal templates';
export let HeaderColId = /*#__PURE__*/function (HeaderColId) {
  HeaderColId["name"] = "name";
  HeaderColId["lastUpdatedOn"] = "lastUpdatedOn";
  return HeaderColId;
}({});
function getTableDefs() {
  const defs = compact([{
    headerItem: {
      id: HeaderColId.name,
      value: 'Template name'
    },
    columnDef: {
      width: [180, 500],
      sortable: true
    }
  }, {
    headerItem: {
      id: HeaderColId.lastUpdatedOn,
      value: 'Last Updated On'
    },
    columnDef: {
      width: [180, 500],
      sortable: true
    }
  }]);
  return {
    columnDefs: defs.map(d => d.columnDef),
    headerItems: defs.map(d => d.headerItem)
  };
}
const sortFunctions = {
  [HeaderColId.name]: _ref => {
    let {
      isAscending
    } = _ref;
    return (a, b) => {
      const retval = ProposalTemplateSummaryModel.sortByTitle(a, b) || sortById(a, b);
      return isAscending ? retval : -retval;
    };
  },
  [HeaderColId.lastUpdatedOn]: _ref2 => {
    let {
      isAscending
    } = _ref2;
    return (a, b) => {
      const retval = sortByLastUpdatedOnDate(a, b) || sortById(a, b);
      return isAscending ? retval : -retval;
    };
  }
};
const sortById = (a, b) => a.id < b.id ? -1 : 1;
function sortByLastUpdatedOnDate(a, b) {
  return a.lastUpdatedOn() - b.lastUpdatedOn();
}
function sortProposalTemplates(config) {
  const {
    templates,
    sort,
    headerItems
  } = config;
  const result = templates.slice();
  if (sort) {
    var _headerItems$sort$col;
    const colId = (_headerItems$sort$col = headerItems[sort.colIndex]) === null || _headerItems$sort$col === void 0 ? void 0 : _headerItems$sort$col.id;
    if (colId) result.sort(sortFunctions[colId]({
      isAscending: sort.sort === 'asc'
    }));
  }
  return result;
}
function CreateTemplateButton() {
  const {
    reportButtonClick
  } = useEventReporter();
  const history = useHistory();
  return /*#__PURE__*/_jsx(Button, {
    "data-testid": "proposal-templates-empty-new-btn",
    intent: Intent.CreateProposalTemplate,
    onClick: () => {
      reportButtonClick('create-template');
      history.push(routes.proposalTemplateStudio);
    },
    children: "Create template"
  });
}
function EmptyState(_ref3) {
  let {
    isRefreshing
  } = _ref3;
  return /*#__PURE__*/_jsx(Page, {
    header: title,
    loading: isRefreshing,
    children: /*#__PURE__*/_jsx(PageEmptyState, {
      "data-testid": "proposal-templates-empty",
      illustration: /*#__PURE__*/_jsx(NoTemplatesIcon, {}),
      title: "Speed up your workflow with Proposal Templates",
      message: 'Tip: a template can also be created by selecting "Save as template" when creating a proposal.',
      actions: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(CreateTemplateButton, {}), /*#__PURE__*/_jsx(ExternalLinkButton, {
          openInNewTab: true,
          testid: "proposal-templates-learn-more",
          underline: "hover",
          href: helpArticles.proposalTemplates(),
          children: "Learn more about Proposal Templates"
        })]
      })
    })
  });
}
function ProposalTemplatesRow(props) {
  const {
    template,
    onUseTemplate,
    onPreview,
    onEdit,
    onRename,
    onRemove
  } = props;
  const testId = 'proposal-templates-row';
  const heapId = 'proposal-templates-row';
  const items = compact([{
    value: /*#__PURE__*/_jsx("div", {
      className: styles.title,
      children: /*#__PURE__*/_jsx(Typography, {
        ellipsis: true,
        weight: "normal",
        "data-testid": "proposal-template-name",
        children: template.title
      })
    })
  }, {
    value: /*#__PURE__*/_jsx("div", {
      className: styles.title,
      children: /*#__PURE__*/_jsx(Typography, {
        ellipsis: true,
        "data-testid": "proposal-template-last-updated-on",
        children: template.formattedLastUpdatedOn()
      })
    })
  }]);
  return /*#__PURE__*/_jsx(TableList.Row, {
    items: items,
    rowId: template.id,
    "data-testid": testId,
    "data-heapid": heapId,
    actionColumn: /*#__PURE__*/_jsxs(TableListActions, {
      testId: "proposal-templates-row-actions",
      menuHeapId: "proposal-templates-row-actions-menu-btn",
      buttonText: "Edit",
      secondaryButtonText: "Use template",
      onClickButton: () => onEdit(template),
      onClickSecondaryButton: () => onUseTemplate(template),
      buttonIntent: Intent.EditProposalTemplate,
      btnHeapId: "".concat(heapId, "-menu-edit-menu-action"),
      children: [/*#__PURE__*/_jsx(DropdownMenu.Item, {
        iconStart: /*#__PURE__*/_jsx(EyeLine, {}),
        "data-testid": "".concat(testId, "-preview-menu-action"),
        "data-heapid": "".concat(heapId, "-menu-preview-menu-action"),
        onClick: () => onPreview(template),
        children: "Preview"
      }), /*#__PURE__*/_jsx(DropdownMenu.Item, {
        iconStart: /*#__PURE__*/_jsx(EditLine, {}),
        "data-testid": "".concat(testId, "-rename-menu-action"),
        "data-heapid": "".concat(heapId, "-menu-rename-menu-action"),
        onClick: () => onRename(template),
        intent: Intent.RenameProposalTemplate,
        children: "Rename"
      }), /*#__PURE__*/_jsx(DropdownMenu.Item, {
        iconStart: /*#__PURE__*/_jsx(TrashLiner24, {}),
        skin: "error",
        "data-testid": "".concat(testId, "-remove-menu-action"),
        "data-heapid": "".concat(heapId, "-menu-remove-menu-action"),
        onClick: () => onRemove(template),
        intent: Intent.RemoveProposalTemplate,
        children: "Remove"
      })]
    })
  });
}
function ProposalTemplatesTable(props) {
  const {
    proposalTemplates,
    legalTermsDocs,
    search,
    onChangeSearch,
    refresh
  } = props;
  const {
    api
  } = useStoreActions();
  const scrollElRef = useRef(null);
  const {
    inputDebounceTime
  } = useAppConfig();
  const overlayContext = useOverlayContext();
  const history = useHistory();
  const {
    getNow
  } = useIoContext();
  const worksAt = useStore(selectors.user.business);
  const {
    doBulkAction
  } = useBulkActionModal();
  const {
    headerItems,
    columnDefs
  } = useMemo(() => getTableDefs(), []);
  const sortIds = useMemo(() => headerItems.map(item => item.id), [headerItems]);
  const defaultSort = useMemo(() => ({
    colIndex: 1,
    sort: 'desc'
  }), []);
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const [selected, setSelected] = useState({});
  const getSelected = useStateInRef(selected);
  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
  const numSelected = useMemo(() => {
    var _countBy$true;
    return (_countBy$true = countBy(selected).true) !== null && _countBy$true !== void 0 ? _countBy$true : 0;
  }, [selected]);
  const onSortChange = sort => {
    changeSort(sort);
  };
  const sortedProposalTemplates = useMemo(() => sortProposalTemplates({
    templates: proposalTemplates,
    sort,
    headerItems
  }), [sort, proposalTemplates, headerItems]);
  const {
    paginatedData,
    onScroll
  } = useInfiniteScroll({
    data: sortedProposalTemplates,
    scrollElRef
  });
  const onToggleAllSelection = useCallback(selectAll => {
    setSelected(proposalTemplates.reduce((acc, template) => Object.assign(acc, {
      [template.id]: selectAll
    }), {}));
  }, [proposalTemplates]);
  function getSelectedProposalTemplates() {
    return Object.entries(selected).filter(_ref4 => {
      let [, isSelected] = _ref4;
      return isSelected;
    }).map(_ref5 => {
      let [proposalTemplateId, _] = _ref5;
      return proposalTemplateId;
    });
  }
  async function bulkRemove(selectedProposalTemplates) {
    await doBulkAction({
      bulkItemIds: selectedProposalTemplates,
      confirmation: {
        title: 'Remove templates',
        primaryButtonLabel: 'Send Request',
        content: /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsxs(Typography, {
            children: ["Are you sure you want to remove", ' ', /*#__PURE__*/_jsx(Typography, {
              "data-testid": "proposal-template-remove-modal-template-name",
              as: "span",
              weight: "bold",
              children: selectedProposalTemplates.length
            }), ' ', "templates?"]
          }), /*#__PURE__*/_jsx(Typography, {
            children: "This action cannot be undone."
          })]
        })
      },
      progressTitle: 'Remove templates',
      actionName: 'removed',
      itemName: 'template',
      canProcessItem: () => true,
      processSingleItem: proposaltemplateId => handlePromise(api.proposalTemplates.remove(proposaltemplateId)),
      onDone: failedProposalTemplateIds => {
        setSelected(failedProposalTemplateIds.reduce((acc, templateId) => _objectSpread(_objectSpread({}, acc), {}, {
          [templateId]: true
        }), {}));
      }
    });
    refresh();
  }
  const onUseTemplate = async template => {
    const draftId = await api.proposalTemplates.createDraft(template.id);
    history.push(routes.draft(draftId));
  };
  const onPreview = async summary => {
    const template = await api.proposalTemplates.get(summary.id);
    const draft = proposalTemplateModel.toPreviewData(template);
    const sampleContact = ContactModel.makeSample({
      createdAt: getNow(),
      vendorId: worksAt === null || worksAt === void 0 ? void 0 : worksAt.id
    });
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: props => /*#__PURE__*/_jsx(ClientPreview, _objectSpread({
        type: "draft",
        draftInfo: {
          draft,
          contactsMap: {},
          sampleContact,
          legalTerms: legalTermsDocs
        }
      }, props))
    });
  };
  const onEdit = template => {
    history.push(routes.proposalTemplate(template.id));
  };
  const onRename = async template => {
    const newName = await overlayContext.showModal({
      content: function RenameModal(_ref6) {
        let {
          dismissModal
        } = _ref6;
        const [newName, setNewName] = useState(template.title);
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          title: "Rename template",
          "data-testid": "proposal-template-rename-modal",
          content: /*#__PURE__*/_jsx(_Fragment, {
            children: /*#__PURE__*/_jsx(TextField, {
              testid: "proposal-template-rename-modal-input",
              name: "Template name",
              value: newName,
              onChange: e => {
                setNewName(e.target.value);
              }
            })
          }),
          primaryButtonLabel: "Save",
          onPrimaryButtonClick: () => dismissModal(newName),
          secondaryButtonLabel: "Cancel",
          onSecondaryButtonClick: () => dismissModal(null),
          onCloseButtonClick: () => dismissModal(null)
        });
      }
    });
    if (newName) {
      api.proposalTemplates.rename(template.id, newName).then(() => refresh()).catch(e => {
        if (e.response.data.error === ServerError.NameAlreadyExists) overlayContext.showErrorToast('A template with this name already exists.', 'duplicate-template-name-error');
      });
    }
  };
  const onRemove = async template => {
    const doRemove = await overlayContext.showModal({
      content: _ref7 => {
        let {
          dismissModal
        } = _ref7;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          title: "Remove template",
          skin: "danger",
          textAlignCenter: false,
          "data-testid": "proposal-template-remove-modal",
          content: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsxs(Typography, {
              children: ["Are you sure you want to remove", ' ', /*#__PURE__*/_jsx(Typography, {
                "data-testid": "proposal-template-remove-modal-template-name",
                as: "span",
                weight: "bold",
                children: template.title
              }), ' ', "from your templates?"]
            }), /*#__PURE__*/_jsx(Typography, {
              children: "This action cannot be undone."
            })]
          }),
          primaryButtonLabel: "Remove template",
          onPrimaryButtonClick: () => dismissModal(true),
          secondaryButtonLabel: "Cancel",
          onSecondaryButtonClick: () => dismissModal(false),
          onCloseButtonClick: () => dismissModal(false)
        });
      }
    });
    if (doRemove) {
      api.proposalTemplates.remove(template.id).then(() => refresh());
    }
  };
  useEffect(() => {
    // this unselects any proposal template that is not found in the newly fetched list of proposal templates
    if (!proposalTemplates || isEmpty(getSelected())) return;
    const ids = proposalTemplates.map(proposalTemplate => proposalTemplate.id);
    const newSelected = mapValues(getSelected(), (origValue, proposalTemplateId) => ids.includes(proposalTemplateId) ? origValue : false);
    setSelected(newSelected);
  }, [proposalTemplates]);
  return /*#__PURE__*/_jsx(Page, {
    "data-testid": "proposal-templates-table",
    header: title,
    ref: scrollElRef,
    actions: /*#__PURE__*/_jsx(CreateTemplateButton, {}),
    subheader: pluralize('templates', proposalTemplates.length, true),
    onScroll: onScroll,
    children: /*#__PURE__*/_jsxs(TableList.Provider, {
      hasActionColumn: true,
      enableRowSelection: true,
      columnDefs: columnDefs,
      selectedRows: selected,
      sort: sort,
      onSortChange: onSortChange,
      onSelectionChange: setSelected,
      numRows: proposalTemplates.length,
      numSelectedRows: numSelected,
      onToggleAllSelection: onToggleAllSelection,
      children: [/*#__PURE__*/_jsxs(PageContentHeader, {
        children: [/*#__PURE__*/_jsx("div", {
          style: {
            marginBlockEnd: '24px'
          },
          children: /*#__PURE__*/_jsxs(FilterPickerGroup, {
            flexWrap: true,
            "data-testid": "proposal-templates-dashboard-filters",
            children: [/*#__PURE__*/_jsx(Searchbox, {
              registerHotkey: true,
              value: search,
              "data-testid": "proposal-templates-search",
              debounceTime: inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500,
              onClear: () => onChangeSearch(''),
              onChange: event => {
                const searchStr = event.target.value;
                onChangeSearch(searchStr);
              }
            }), /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(Typography, {
                className: styles.selectedItems,
                "data-testid": "proposalTemplate-dashboard-bulk-counter",
                children: numSelected > 0 ? "".concat(pluralize('template', numSelected, true), " selected") : ''
              }), /*#__PURE__*/_jsx(DropdownMenu, {
                onOpenChange: setBulkActionsOpen,
                target: /*#__PURE__*/_jsx(ChipButton, {
                  "data-testid": "proposal-templates-bulk-actions",
                  suffixIcon: bulkActionsOpen ? /*#__PURE__*/_jsx(WideArrowUpward, {}) : /*#__PURE__*/_jsx(WideArrowDownward, {}),
                  highlight: bulkActionsOpen,
                  disabled: numSelected === 0,
                  children: "Bulk actions"
                }),
                children: /*#__PURE__*/_jsx(DropdownMenu.Item, {
                  iconStart: /*#__PURE__*/_jsx(TrashLiner24, {}),
                  skin: "error",
                  intent: Intent.RemoveProposalTemplate,
                  "data-testid": "proposal-templates-bulk-remove",
                  onClick: () => bulkRemove(getSelectedProposalTemplates()),
                  children: "Remove"
                })
              })]
            })]
          })
        }), /*#__PURE__*/_jsx(TableList.Header, {
          items: headerItems,
          "data-testid": "proposal-templates-table-header"
        })]
      }), paginatedData.map(proposalTemplate => /*#__PURE__*/_jsx(ProposalTemplatesRow, {
        template: proposalTemplate,
        onUseTemplate: onUseTemplate,
        onPreview: onPreview,
        onEdit: onEdit,
        onRename: onRename,
        onRemove: onRemove
      }, proposalTemplate.id))]
    })
  });
}
export function ProposalTemplates() {
  const {
    api,
    fetchLegalTerms
  } = useStoreActions();
  const shouldRefresh = useRef(false);
  const refreshRef = useRef(() => {});
  const {
    urlState,
    changeUrlState
  } = useStateFromUrl({
    search: {
      defaultValue: ''
    }
  });
  const {
    search
  } = urlState;
  useSetDocumentTitle(title);
  useEffect(() => {
    if (shouldRefresh.current) refreshRef.current();
  }, [urlState]);
  return /*#__PURE__*/_jsx(RestfulWrapper, {
    fetch: () => Promise.all([api.proposalTemplates.getAll().then(templateSummary => templateSummary.filter(t => t.lowercaseTitle().includes(search.toLowerCase()))), fetchLegalTerms().then(legalTerms => {
      if (!legalTerms) throw makeSentryIgnoredError('legalTermsError');
      return legalTerms;
    })]).then(_ref8 => {
      let [proposalTemplates, legalTermsDocs] = _ref8;
      return {
        proposalTemplates,
        legalTermsDocs
      };
    }),
    renderLoader: () => /*#__PURE__*/_jsx(PageLoadingState, {
      header: title,
      loading: true
    }),
    renderError: _ref9 => {
      let {
        refresh
      } = _ref9;
      return /*#__PURE__*/_jsx(PageErrorState, {
        title: title,
        onRefresh: refresh
      });
    },
    renderChild: _ref10 => {
      let {
        data: {
          proposalTemplates,
          legalTermsDocs
        },
        loading,
        refresh
      } = _ref10;
      refreshRef.current = refresh;
      return proposalTemplates.length === 0 && !search ? /*#__PURE__*/_jsx(EmptyState, {
        isRefreshing: loading
      }) : /*#__PURE__*/_jsx(ProposalTemplatesTable, {
        proposalTemplates: proposalTemplates,
        legalTermsDocs: legalTermsDocs,
        refresh: refresh,
        isRefreshing: loading,
        search: search,
        onChangeSearch: search => {
          shouldRefresh.current = true;
          changeUrlState({
            search
          });
        }
      });
    }
  });
}